// Shared API utility functions

import { AssistantsApiResponse } from "../api/assistants/route";
import { SubmitData } from "../components/DynamicForm";
import { Project } from "../generated/backendModels";
import {
  Agent,
  AgentConfig,
  Datasource,
  DatasourceConfig,
  EntityStatus,
  IngestionLiveStats,
  IngestionRun,
} from "../onboard/types";
import { apiFetch } from "./apiFetch";

export interface ToolConfig {
  type?: string;
  datasource?: string;
  agent?: string;
  display_name?: string;
}

export interface AgentOption {
  value: string;
  label: string;
  description?: string;
  welcome_message?: string;
  examples?: string[];
  authentication?: {
    enabled: boolean;
    is_owner: boolean;
    allowed_roles?: string[];
    allowed_groups?: string[];
    owner_roles?: string[];
    owner_groups?: string[];
  };
  use_shared_workspace?: boolean;
  datasource_selection_available?: boolean;
  history_enabled?: boolean;
  chat_attachments_enabled?: boolean;
  show_agent_description?: boolean;
  tools?: ToolConfig[];
}
export interface SharepointAccessResponse {
  has_access: boolean;
  status?: number;
  error?: string;
}

export interface AssistantsPaginationParams {
  limit?: number;
  offset?: number;
  search?: string;
}

export async function fetchAssistants(
  accessToken?: string,
  spaceName?: string,
  pagination?: AssistantsPaginationParams,
): Promise<AssistantsApiResponse> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  if (spaceName) {
    headers["X-Space-Name"] = spaceName;
  }

  const params = new URLSearchParams();
  if (pagination) {
    if (pagination.limit != null)
      params.append("limit", String(pagination.limit));
    if (pagination.offset != null)
      params.append("offset", String(pagination.offset));
    if (pagination.search) params.append("search", pagination.search);
  }

  const queryString = params.toString();
  const separator = queryString ? "?" : "";

  try {
    const response = await apiFetch(
      `/api/assistants${separator}${queryString}`,
      {
        headers,
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch assistants");
    }

    const data: AssistantsApiResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching assistants:", error);
    throw error;
  }
}

/**
 * Status-aware result of looking up a single assistant by name.
 *
 * Feature #10286672: a bare `null` return could not distinguish an auth failure
 * (401) from a genuinely missing assistant (404), so the deep-link handler
 * bounced every auth hiccup to the home page. This discriminated result lets the
 * caller re-authenticate on `unauthorized`/`error` and only redirect home on a
 * legitimate `not_found`.
 */
export type AssistantLookupResult =
  | { status: "ok"; agent: AgentOption }
  | { status: "unauthorized" }
  | { status: "not_found" }
  | { status: "error" };

export async function fetchAssistantResult(
  name: string,
  accessToken?: string,
  spaceName?: string,
): Promise<AssistantLookupResult> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  if (spaceName) {
    headers["X-Space-Name"] = spaceName;
  }

  try {
    const response = await apiFetch(
      `/api/assistants/${encodeURIComponent(name)}`,
      {
        headers,
      },
    );

    if (!response.ok) {
      // 401 → auth failure (no/expired token). 403/404 → the assistant is not
      // available to this user (treated as "not found"). Anything else (5xx,
      // etc.) is a transient error. Never collapse these into one outcome.
      if (response.status === 401) {
        return { status: "unauthorized" };
      }
      if (response.status === 403 || response.status === 404) {
        return { status: "not_found" };
      }
      return { status: "error" };
    }

    const data = await response.json();
    return {
      status: "ok",
      agent: {
        value: data.name,
        label: data.display_name || data.name,
        description: data.description,
        authentication: data.authentication,
        welcome_message: data.welcome_message,
        examples: data.examples,
        tools: data.tools,
        use_shared_workspace: data.use_shared_workspace,
        datasource_selection_available: data.datasource_selection_available,
        history_enabled: data.history_enabled,
        chat_attachments_enabled: data.chat_attachments_enabled,
        show_agent_description: data.show_agent_description,
      },
    };
  } catch (error) {
    console.error("Error fetching assistant by name:", error);
    return { status: "error" };
  }
}

/**
 * Thin wrapper preserved for callers that only need the agent-or-null shape.
 * Prefer {@link fetchAssistantResult} when you need to react to auth vs
 * not-found differently.
 */
export async function fetchAssistantByName(
  name: string,
  accessToken?: string,
  spaceName?: string,
): Promise<AgentOption | null> {
  const result = await fetchAssistantResult(name, accessToken, spaceName);
  return result.status === "ok" ? result.agent : null;
}

export interface DatasourceTreeEntry {
  name: string;
  display_name?: string | null;
  /** Chain of sub-agent names leading to this datasource, root-first. Empty
   * when the assistant retrieves from it directly. */
  via: string[];
}

/**
 * Datasources reachable from an assistant, including through `type: agent`
 * sub-agent tools (e.g. tar-agent, which has no direct retrieval tools).
 * Unlike the assistant list/detail endpoints, this isn't filtered by
 * ownership or AG-UI exposure — a sub-agent used only internally still
 * surfaces its datasources here.
 */
export async function fetchAssistantDatasourceTree(
  name: string,
  accessToken?: string,
  spaceName?: string,
): Promise<DatasourceTreeEntry[]> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
  if (spaceName) headers["X-Space-Name"] = spaceName;

  const response = await apiFetch(
    `/api/assistants/${encodeURIComponent(name)}/datasource-tree`,
    { headers },
  );
  if (!response.ok) {
    throw new Error("Failed to fetch datasource tree");
  }
  const data = await response.json();
  return data.datasources ?? [];
}

export async function checkSharepointAccess(
  siteUrl: string,
  token?: string,
): Promise<SharepointAccessResponse> {
  try {
    const response = await apiFetch(
      `/api/sharepoint/sp-access?url=${encodeURIComponent(siteUrl)}`,
      {
        headers: {
          ...buildAuthHeader(token),
        },
      },
    );
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message =
        (data && (data.error as string)) ||
        response.statusText ||
        "Failed to check SharePoint access";
      throw new Error(message);
    }

    return data as SharepointAccessResponse;
  } catch (error) {
    console.error("Error checking SharePoint access:", error);
    throw error;
  }
}

export interface ProjectSubmissionResponse {
  submitted: boolean;
  pr_link?: string;
}

export async function submitProject(
  data: SubmitData<Project>,
): Promise<ProjectSubmissionResponse> {
  try {
    const response = await apiFetch(`/api/onboarding/self/project`, {
      method: "POST",
      body: JSON.stringify({ ...data }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      const result = await response.json();
      console.log("Project submitted successfully:", result);
      return {
        submitted: true,
        pr_link: result.pr_link,
      };
    } else {
      const errorResult = await response.json();
      console.error("Failed to submit project:", errorResult);
      return {
        submitted: false,
      };
    }
  } catch (error) {
    console.error("Error submitting project:", error);
    throw error;
  }
}

// ============================================
// Onboarding API
// ============================================

// Helper to build authorization header
// TODO: Update callers to pass token from useAuth().getToken() when backend auth is implemented
function buildAuthHeader(token?: string): Record<string, string> {
  if (!token) {
    return {};
  }
  return {
    Authorization: `Bearer ${token}`,
  };
}

// ----- Agent API -----

export interface CreateAgentRequest {
  name: string;
  description: string;
  status?: EntityStatus;
  schema_version?: string;
  config: AgentConfig;
  datasourceIds?: string[];
}

export interface UpdateAgentRequest {
  name?: string;
  description?: string;
  status?: EntityStatus;
  schema_version?: string;
  config?: Partial<AgentConfig>;
  datasourceIds?: string[];
}

export interface PaginationParams {
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: string;
  search?: string;
}

export interface FetchAgentsResponse {
  agents: Agent[];
  total: number;
}

export async function fetchAgents(
  includeInactive: boolean = false,
  token?: string,
  pagination?: PaginationParams,
): Promise<FetchAgentsResponse> {
  const params = new URLSearchParams();
  params.append("status", "ACTIVE");
  if (includeInactive) {
    params.append("status", "DELETED");
  }
  if (pagination) {
    if (pagination.limit != null)
      params.append("limit", String(pagination.limit));
    if (pagination.offset != null)
      params.append("offset", String(pagination.offset));
    if (pagination.sort_by) params.append("sort_by", pagination.sort_by);
    if (pagination.sort_order)
      params.append("sort_order", pagination.sort_order);
    if (pagination.search) params.append("search", pagination.search);
  }
  const url = `/api/management/assistants?${params.toString()}`;
  const response = await apiFetch(url, {
    headers: {
      ...buildAuthHeader(token),
    },
  });
  if (!response.ok) {
    throw new Error("Failed to fetch agents");
  }
  const data = await response.json();
  return {
    agents: data.assistants || [],
    total: data.total ?? 0,
  };
}

export async function fetchAgentById(
  id: string,
  token?: string,
): Promise<Agent | null> {
  const url = `/api/management/assistants/${id}`;

  const response = await apiFetch(url, {
    headers: {
      ...buildAuthHeader(token),
    },
  });
  if (response.status === 404) {
    return null;
  }
  if (response.status === 403) {
    throw new Error("Access denied");
  }
  if (!response.ok) {
    throw new Error("Failed to fetch agent");
  }
  return await response.json();
}

export async function createAgent(
  data: CreateAgentRequest,
  token?: string,
): Promise<Agent> {
  const url = `/api/management/assistants`;

  const response = await apiFetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeader(token),
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || errorData.detail || "Failed to create agent",
    );
  }
  return await response.json();
}

export async function updateAgent(
  id: string,
  data: UpdateAgentRequest,
  token?: string,
): Promise<Agent | null> {
  const url = `/api/management/assistants/${id}`;

  const response = await apiFetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeader(token),
    },
    body: JSON.stringify(data),
  });
  if (response.status === 404) {
    return null;
  }
  if (response.status === 403) {
    throw new Error(
      "Access denied: You don't have permission to modify this agent",
    );
  }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || errorData.detail || "Failed to update agent",
    );
  }
  return await response.json();
}

export async function toggleAgentActive(
  id: string,
  currentlyActive: boolean,
  token?: string,
): Promise<Agent> {
  const newStatus: EntityStatus = currentlyActive ? "DELETED" : "ACTIVE";

  const response = await apiFetch(`/api/management/assistants/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeader(token),
    },
    body: JSON.stringify({ status: newStatus }),
  });
  if (response.status === 404) {
    throw new Error("Agent not found");
  }
  if (response.status === 403) {
    throw new Error(
      "Access denied: You don't have permission to modify this agent",
    );
  }
  if (!response.ok) {
    throw new Error("Failed to toggle agent status");
  }
  return await response.json();
}

// ----- Datasource API -----

export interface CreateDatasourceRequest {
  name: string;
  description: string;
  status?: EntityStatus;
  setup_status?: string;
  schema_version?: string;
  config: DatasourceConfig;
}

export interface UpdateDatasourceRequest {
  name?: string;
  description?: string;
  status?: EntityStatus;
  setup_status?: string;
  schema_version?: string;
  config?: Partial<DatasourceConfig>;
}

export interface FetchDatasourcesResponse {
  datasources: Datasource[];
  total: number;
}

export async function fetchDatasources(
  includeInactive: boolean = false,
  token?: string,
  pagination?: PaginationParams,
): Promise<FetchDatasourcesResponse> {
  const params = new URLSearchParams();
  params.append("status", "ACTIVE");
  if (includeInactive) {
    params.append("status", "DELETED");
  }
  if (pagination) {
    if (pagination.limit != null)
      params.append("limit", String(pagination.limit));
    if (pagination.offset != null)
      params.append("offset", String(pagination.offset));
    if (pagination.sort_by) params.append("sort_by", pagination.sort_by);
    if (pagination.sort_order)
      params.append("sort_order", pagination.sort_order);
    if (pagination.search) params.append("search", pagination.search);
  }
  const url = `/api/management/datasources?${params.toString()}`;
  const response = await apiFetch(url, {
    headers: {
      ...buildAuthHeader(token),
    },
  });
  if (!response.ok) {
    throw new Error("Failed to fetch datasources");
  }
  const data = await response.json();
  return {
    datasources: data.datasources || [],
    total: data.total ?? 0,
  };
}

export async function fetchDatasourceById(
  id: string,
  token?: string,
): Promise<Datasource | null> {
  const url = `/api/management/datasources/${id}`;

  const response = await apiFetch(url, {
    headers: {
      ...buildAuthHeader(token),
    },
  });
  if (response.status === 404) {
    return null;
  }
  if (response.status === 403) {
    throw new Error("Access denied");
  }
  if (!response.ok) {
    throw new Error("Failed to fetch datasource");
  }
  return await response.json();
}

export async function createDatasource(
  data: CreateDatasourceRequest,
  token?: string,
): Promise<Datasource> {
  const url = `/api/management/datasources`;

  const response = await apiFetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeader(token),
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || errorData.detail || "Failed to create datasource",
    );
  }
  return await response.json();
}

export async function updateDatasource(
  id: string,
  data: UpdateDatasourceRequest,
  token?: string,
): Promise<Datasource | null> {
  const url = `/api/management/datasources/${id}`;

  const response = await apiFetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeader(token),
    },
    body: JSON.stringify(data),
  });
  if (response.status === 404) {
    return null;
  }
  if (response.status === 403) {
    throw new Error(
      "Access denied: You don't have permission to modify this datasource",
    );
  }
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || errorData.detail || "Failed to update datasource",
    );
  }
  return await response.json();
}

export async function toggleDatasourceActive(
  id: string,
  currentlyActive: boolean,
  token?: string,
): Promise<Datasource> {
  const newStatus: EntityStatus = currentlyActive ? "DELETED" : "ACTIVE";

  const response = await apiFetch(`/api/management/datasources/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeader(token),
    },
    body: JSON.stringify({ status: newStatus }),
  });
  if (response.status === 404) {
    throw new Error("Datasource not found");
  }
  if (response.status === 403) {
    throw new Error(
      "Access denied: You don't have permission to modify this datasource",
    );
  }
  if (!response.ok) {
    throw new Error("Failed to toggle datasource status");
  }
  return await response.json();
}

// ----- Test Access API -----

export interface VerifyAccessResponse {
  has_access: boolean;
  datasource: Datasource;
  error?: string;
}

export async function verifyDatasourceAccess(
  id: string,
  token?: string,
): Promise<VerifyAccessResponse> {
  const response = await apiFetch(
    `/api/management/datasources/${id}/verify-access`,
    {
      method: "POST",
      headers: {
        ...buildAuthHeader(token),
      },
    },
  );
  if (response.status === 404) {
    throw new Error("Datasource not found");
  }
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Failed to test access" }));
    throw new Error(error.detail || "Failed to test access");
  }
  return await response.json();
}

// ----- Static Datasources API -----

export interface FetchStaticDatasourcesResponse {
  datasources: Datasource[];
  total: number;
}

export async function fetchStaticDatasources(
  token?: string,
  pagination?: PaginationParams,
): Promise<FetchStaticDatasourcesResponse> {
  try {
    const params = new URLSearchParams();
    if (pagination) {
      if (pagination.limit != null)
        params.append("limit", String(pagination.limit));
      if (pagination.offset != null)
        params.append("offset", String(pagination.offset));
      if (pagination.sort_by) params.append("sort_by", pagination.sort_by);
      if (pagination.sort_order)
        params.append("sort_order", pagination.sort_order);
      if (pagination.search) params.append("search", pagination.search);
    }
    const qs = params.toString();
    const url = `/api/management/datasources/static${qs ? `?${qs}` : ""}`;
    const response = await apiFetch(url, {
      headers: {
        ...buildAuthHeader(token),
      },
    });
    if (!response.ok) {
      return { datasources: [], total: 0 };
    }
    const data = await response.json();
    return { datasources: data.datasources || [], total: data.total ?? 0 };
  } catch {
    return { datasources: [], total: 0 };
  }
}

// ----- Available Datasources API -----

export async function fetchAvailableDatasources(
  token?: string,
): Promise<string[]> {
  const response = await apiFetch(`/api/management/datasources?status=ACTIVE`, {
    headers: {
      ...buildAuthHeader(token),
    },
  });
  if (!response.ok) {
    throw new Error("Failed to fetch available datasources");
  }
  const data = await response.json();
  const datasources: Datasource[] = data.datasources || [];
  return datasources.map((ds) => ds.name);
}

// ----- Ingestion API -----

export interface TriggerIngestionResponse {
  success: boolean;
  message: string;
  runId: string;
  runUrl: string;
  state: string;
}

export interface IngestionStatusResponse {
  runId: string;
  state: string;
  result?: string;
  status?: string;
  runUrl?: string;
  createdDate?: string;
  finishedDate?: string;
  liveStats?: IngestionLiveStats | null;
}

export async function triggerIngestion(
  datasourceName: string,
  datasourceType: "auto" | "dynamic" | "static" = "auto",
  token?: string,
): Promise<TriggerIngestionResponse> {
  const response = await apiFetch(
    `/api/management/pipeline/ingestion/trigger`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...buildAuthHeader(token),
      },
      body: JSON.stringify({
        datasourceName: datasourceName,
        datasourceType,
      }),
    },
  );
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Failed to trigger ingestion" }));
    const errorMessage =
      typeof error.detail === "string"
        ? error.detail
        : error.detail?.error ||
          error.error ||
          JSON.stringify(error) ||
          "Failed to trigger ingestion";
    throw new Error(errorMessage);
  }
  const data = await response.json();
  return {
    success: data.success,
    message: data.message,
    runId: String(data.runId),
    runUrl: data.runUrl,
    state: data.state,
  };
}

export async function getIngestionStatus(
  runId: string,
  token?: string,
): Promise<IngestionStatusResponse> {
  const response = await apiFetch(
    `/api/management/pipeline/ingestion/runs/${runId}`,
    {
      headers: {
        ...buildAuthHeader(token),
      },
    },
  );
  if (!response.ok) {
    throw new Error("Failed to get ingestion status");
  }
  const data = await response.json();
  return {
    runId: String(data.runId),
    state: data.state,
    result: data.result,
    status: data.status,
    runUrl: data.runUrl,
    createdDate: data.createdDate,
    finishedDate: data.finishedDate,
    liveStats: data.liveStats ?? null,
  };
}

export async function getLatestIngestionRun(
  datasourceId: string,
  token?: string,
): Promise<IngestionRun | null> {
  const response = await apiFetch(
    `/api/management/datasources/${datasourceId}/ingestion-runs/latest`,
    {
      headers: {
        ...buildAuthHeader(token),
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to get latest ingestion run");
  }

  const data = await response.json();
  return (data.lastRun ?? null) as IngestionRun | null;
}

// ----- Projects & MCP API -----

export interface ProjectSummary {
  name: string;
  description: string;
}

// Shape returned by JSON-RPC tools/list (MCP spec). inputSchema is JSON Schema.
export interface McpTool {
  name: string;
  description?: string;
  inputSchema?: {
    properties?: Record<string, { type?: string; description?: string }>;
    required?: string[];
  };
}

export async function fetchProjects(
  accessToken?: string,
): Promise<{ projects: ProjectSummary[] }> {
  const res = await apiFetch("/api/projects", {
    headers: { ...buildAuthHeader(accessToken) },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch projects: ${res.status}`);
  }
  return res.json();
}

// Internal: POST a JSON-RPC request to the real MCP endpoint.
async function mcpJsonRpc(
  projectName: string,
  method: string,
  params: Record<string, unknown>,
  accessToken?: string,
): Promise<{ status: number; durationMs: number; text: string; ok: boolean }> {
  const body = { jsonrpc: "2.0", id: 1, method, params };
  const start = performance.now();
  const res = await apiFetch(`/api/mcp/${projectName}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json, text/event-stream",
      ...buildAuthHeader(accessToken),
    },
    body: JSON.stringify(body),
  });
  const durationMs = Math.round(performance.now() - start);
  return { status: res.status, durationMs, text: await res.text(), ok: res.ok };
}

/**
 * Parse a FastMCP response body into a JSON-RPC object.
 *
 * FastMCP's StreamableHTTP transport may answer a single request as either
 * plain JSON or a Server-Sent Events stream (`event: message\ndata: {...}`),
 * depending on its negotiation. Plain JSON.parse() throws on the SSE framing
 * ("Unexpected identifier 'event'"), so detect SSE and extract the last
 * `data:` payload (the JSON-RPC response).
 */
export function parseMcpResponse(text: string): Record<string, unknown> {
  const trimmed = text.trimStart();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    return JSON.parse(trimmed);
  }
  // SSE: collect the JSON carried on `data:` lines (last data block wins).
  const dataLines = text
    .split(/\r?\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice("data:".length).trim())
    .filter(Boolean);
  if (dataLines.length === 0) {
    throw new Error(`Unrecognized MCP response: ${text.slice(0, 200)}`);
  }
  return JSON.parse(dataLines[dataLines.length - 1]);
}

export async function listMcpTools(
  projectName: string,
  accessToken?: string,
): Promise<McpTool[]> {
  const r = await mcpJsonRpc(projectName, "tools/list", {}, accessToken);
  if (!r.ok) {
    throw new Error(`tools/list failed: HTTP ${r.status} ${r.text}`);
  }
  const parsed = parseMcpResponse(r.text);
  if (parsed.error) {
    throw new Error(`tools/list error: ${JSON.stringify(parsed.error)}`);
  }
  return (parsed.result as { tools?: McpTool[] } | undefined)?.tools ?? [];
}

export interface McpInvokeResult {
  ok: boolean;
  status: number;
  durationMs: number;
  result?: unknown;
  errorMessage?: string;
}

export async function invokeMcpTool(
  projectName: string,
  toolName: string,
  args: Record<string, unknown>,
  accessToken?: string,
): Promise<McpInvokeResult> {
  const r = await mcpJsonRpc(
    projectName,
    "tools/call",
    { name: toolName, arguments: args },
    accessToken,
  );
  if (!r.ok) {
    return {
      ok: false,
      status: r.status,
      durationMs: r.durationMs,
      errorMessage: r.text,
    };
  }
  // FastMCP returns JSON-RPC (as plain JSON or SSE); surface payload + errors.
  try {
    const parsed = parseMcpResponse(r.text);
    if (parsed.error) {
      return {
        ok: false,
        status: r.status,
        durationMs: r.durationMs,
        errorMessage: JSON.stringify(parsed.error),
        result: parsed,
      };
    }
    return {
      ok: true,
      status: r.status,
      durationMs: r.durationMs,
      result: parsed,
    };
  } catch {
    return {
      ok: true,
      status: r.status,
      durationMs: r.durationMs,
      result: r.text,
    };
  }
}
